import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Download } from 'lucide-react';
import { useAuthStore } from '@auth/authStore';
import { getReportList } from '@entities';
import { useAsyncResource, useRegisterFilters, useScopeOptions, useUrlAssetScope } from '@hooks';
import { FilterBar, TablePagination } from '@components';
import RegisterTable from '@/shared/components/RegisterTable/RegisterTable';
import { downloadCSV } from '@shared/utils/exportCSV';
import {
  normalizeReport, attachReportLocations, reportFilters, reportSearchText, reportInScope, sortReports, reportExportRows,
} from '@/entities/report/report.model';
import { reportColumns } from './report.columns';
import ReportDetail from './components/ReportDetail';
import styles from './ReportBrowser.module.css';

function ReportLibrary({ companyId, siteId, assetId }) {
  const [params, setParams] = useSearchParams();
  const locations = useScopeOptions();
  const resource = useAsyncResource(async () => {
    const rows = await getReportList(companyId);
    if (!Array.isArray(rows)) throw new Error('The report API must return a list.');
    return rows.map(normalizeReport);
  }, [companyId], { initialData: [] });

  const locatedRows = useMemo(() => attachReportLocations(
    resource.data, locations.assets, locations.sites
  ), [resource.data, locations.assets, locations.sites]);
  const rows = useMemo(() => locatedRows.filter(
    row => reportInScope(row, siteId, assetId, companyId)
  ), [locatedRows, siteId, assetId, companyId]);
  const unresolved = locatedRows.filter(row => row.companyId === String(companyId).trim() &&
    (!assetId || row.assetId === String(assetId).trim()) && row.locationStatus !== 'resolved').length;

  const filters = useRegisterFilters({
    namespace: 'reports', items: rows, definitions: reportFilters, getSearchText: reportSearchText,
  });
  const [sort, setSort] = useState({ key: 'date', direction: 'desc' });
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState({ key: '', page: 0 });
  const sorted = useMemo(() => sortReports(filters.filtered, sort, reportColumns), [filters.filtered, sort]);
  const pageKey = JSON.stringify([filters.query, filters.values, sort, pageSize]);
  const lastPage = Math.max(0, Math.ceil(sorted.length / pageSize) - 1);
  const page = pagination.key === pageKey ? Math.min(pagination.page, lastPage) : 0;
  const visible = sorted.slice(page * pageSize, (page + 1) * pageSize);
  const selectedKey = params.get('reports.selected');
  const matches = selectedKey ? rows.filter(row => row.key === selectedKey) : [];
  const selected = matches.length === 1 ? matches[0] : null;

  function select(key) {
    setParams(previous => {
      const next = new URLSearchParams(previous);
      if (key) next.set('reports.selected', key);
      else next.delete('reports.selected');
      return next;
    });
  }

  if (resource.isLoading || locations.isLoading) return <div className={styles.feedback} role="status">Loading report library…</div>;
  if (resource.error) return <div className={styles.feedback}>
    <p role="alert">{resource.error}</p>
    <button type="button" className={styles.button} onClick={() => resource.reload()}>Retry reports</button>
  </div>;

  if (siteId && locations.error) return <div className={styles.feedback}>
    <p role="alert">Reports loaded, but site scope cannot be resolved because location data failed: {locations.error}</p>
    <button type="button" className={styles.button} onClick={() => locations.reload()}>Retry locations</button>
  </div>;

  return <>
    <FilterBar filters={filters} noun="reports" actions={
      <button type="button" className={styles.button} disabled={!sorted.length} onClick={() => {
        downloadCSV(reportExportRows(sorted),
          `${siteId || 'allSites'}-${assetId || 'allAssets'}-reports.csv`);
      }}><Download size={16} aria-hidden="true" /> Export metadata</button>
    } />
    {locations.error && <p className={styles.notice}>
      Location data is unavailable. Asset IDs are shown; site-dependent actions are unavailable.
      {' '}<button type="button" className={styles.link} onClick={() => locations.reload()}>Retry names</button>
    </p>}
    {unresolved > 0 && !locations.error && <p className={styles.notice} role="status">
      {unresolved} report(s) could not be assigned to a single site from the asset list.
      {siteId ? ' These reports are excluded from this site scope.' : ' They remain listed with their asset IDs.'}
    </p>}
    {selectedKey && !selected && <div className={styles.notice} role="status">
      <p>{matches.length > 1 ? 'This report identity matches several records; select a uniquely identified report.' :
        'The selected report is unavailable in this location scope.'}</p>
      <button type="button" className={styles.link} onClick={() => select(null)}>Dismiss selection</button>
    </div>}
    <div className={styles.tableArea}>
      {sorted.length ? <RegisterTable rows={visible} columns={reportColumns} sort={sort}
        onSortChange={setSort} getRowKey={row => row.key} selectedKey={selectedKey}
        onOpen={row => select(row.key)} caption="Report library"
        renderCell={(row, column) => {
          if (column.key === 'number') return <>{row.number || 'Report without reference'}<small>Report ID: {row.id}</small></>;
          if (column.key === 'location') return <>{row.assetLabel}<small>{row.siteLabel}</small></>;
          if (column.key === 'type') return <span className={styles.type}>{row.type}</span>;
          if (column.key === 'technique') return <span className={styles.techniques}>{column.value(row)}</span>;
          return column.value(row);
        }} /> : <div className={styles.feedback}>
        <p>{rows.length ? 'No reports match these filters.' : 'No reports are listed in this scope.'}</p>
        {rows.length > 0 && <button type="button" className={styles.button} onClick={filters.clear}>Clear search and filters</button>}
      </div>}
    </div>
    <TablePagination count={sorted.length} page={page} rowsPerPage={pageSize}
      onPageChange={(_event, nextPage) => setPagination({ key: pageKey, page: nextPage })}
      onRowsPerPageChange={event => setPageSize(Number(event.target.value))}
      rowsPerPageOptions={[25, 50, 100]} />
    {selected && <ReportDetail key={selected.key} report={selected} companyId={companyId}
      onClose={() => select(null)} />}
  </>;
}

export default function ReportBrowser() {
  const companyId = useAuthStore(state => state.companyID);
  const { siteId, assetId } = useUrlAssetScope();
  return <section className={styles.page}>
    <header className={styles.heading}>
      <span>REPORT BROWSER</span><h1>Report library</h1>
      <p>Search reports across the selected location. Technique is shown only where the recorded report type identifies RVI or CVI.</p>
    </header>
    {companyId ? <ReportLibrary key={JSON.stringify([companyId, siteId, assetId])}
      companyId={companyId} siteId={siteId} assetId={assetId} /> :
      <p className={styles.feedback}>Select a company to view reports.</p>}
  </section>;
}